import React, { useEffect, useRef, useState, type Dispatch, type SetStateAction } from "react";
import { ArrowUp, LoaderCircle, Mic, Plus, Square } from "lucide-react";
import type { SelectedSkill, SkillDefinition, ThinkingMode } from "../../api/types.js";
import { ComposerMenu } from "./ComposerMenu.js";
import { ProjectChip } from "./ProjectChip.js";
import { RetrievalSelector } from "./RetrievalSelector.js";
import { SkillSlashMenu } from "./SkillSlashMenu.js";
import { SkillIntentChip } from "./SkillIntentChip.js";
import { ThinkingModeSelector } from "./ThinkingModeSelector.js";
import { useComposerMenu } from "./useComposerMenu.js";

export function Composer({
  prompt,
  setPrompt,
  submitting,
  settling,
  running,
  plusOpen,
  setPlusOpen,
  onSubmit,
  onStop,
  onSaveLinkAsset,
  onUploadAsset,
  onAttachRecent,
  skills,
  onManageSkills,
  selectedSkill,
  setSelectedSkill,
  thinkingMode,
  setThinkingMode
}: {
  prompt: string;
  setPrompt: (value: string) => void;
  submitting: boolean;
  settling: boolean;
  running: boolean;
  plusOpen: boolean;
  setPlusOpen: (value: boolean) => void;
  onSubmit: (event?: React.FormEvent) => void;
  onStop: () => void;
  onSaveLinkAsset: () => void;
  onUploadAsset: (file: File) => Promise<void>;
  onAttachRecent: (title: string) => void;
  skills: SkillDefinition[];
  onManageSkills: () => void;
  selectedSkill: SelectedSkill | null;
  setSelectedSkill: Dispatch<SetStateAction<SelectedSkill | null>>;
  thinkingMode: ThinkingMode;
  setThinkingMode: (mode: ThinkingMode) => void;
}) {
  const [retrievalMode, setRetrievalMode] = useState("摘要级检索");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const plusTriggerRef = useRef<HTMLButtonElement>(null);
  const plusMenuRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const slashMenuRef = useRef<HTMLDivElement>(null);
  const plusMenu = useComposerMenu({ open: plusOpen, setOpen: setPlusOpen, triggerRef: plusTriggerRef, menuRef: plusMenuRef });
  const [slashDismissed, setSlashDismissed] = useState(false);
  const [slashIndex, setSlashIndex] = useState(0);
  const slashMatch = slashDismissed ? null : prompt.match(/^\/([^\s]*)$/);
  const slashQuery = (slashMatch?.[1] || "").toLowerCase();
  const slashSkills = slashMatch
    ? skills
      .map((skill, sourceIndex) => {
        const name = skill.name.toLowerCase();
        const displayName = skill.displayName.toLowerCase();
        const score = !slashQuery || name === slashQuery || displayName === slashQuery ? 0
          : name.startsWith(slashQuery) || displayName.startsWith(slashQuery) ? 1
            : name.includes(slashQuery) || displayName.includes(slashQuery) ? 2
              : -1;
        return { skill, sourceIndex, score };
      })
      .filter((entry) => entry.score >= 0)
      .sort((a, b) => a.score - b.score || a.sourceIndex - b.sourceIndex)
      .map((entry) => entry.skill)
    : [];
  const slashOpen = Boolean(slashMatch && slashSkills.length);
  useEffect(() => { setSlashIndex(0); }, [slashQuery, skills]);
  useEffect(() => {
    if (!slashOpen) return;
    const dismissOnOutsidePointer = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target || textareaRef.current?.contains(target) || slashMenuRef.current?.contains(target)) return;
      setSlashDismissed(true);
    };
    document.addEventListener("pointerdown", dismissOnOutsidePointer);
    return () => document.removeEventListener("pointerdown", dismissOnOutsidePointer);
  }, [slashOpen]);

  const selectedSkillFromDefinition = (skill: SkillDefinition): SelectedSkill => ({
    name: skill.name,
    displayName: skill.displayName,
    packSlug: skill.packSlug,
    packDisplayName: skill.packDisplayName
  });

  const selectSlashSkill = (skill: SkillDefinition) => {
    const remainingPrompt = slashMatch ? prompt.slice(slashMatch[0].length).replace(/^\s+/, "") : prompt;
    setSelectedSkill(selectedSkillFromDefinition(skill));
    setPrompt(remainingPrompt);
    setSlashDismissed(true);
  };
  // 中文 IME 组合态守卫:compositionstart/end 追踪组合过程,配合原生 isComposing 与
  // keyCode 229(浏览器在组合中对功能键上报的占位码),覆盖 Chromium 之外(如 Safari)
  // 确认候选那次 Enter 上 isComposing 不可靠的情况。
  const composingRef = useRef(false);
  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const native = event.nativeEvent;
    if (composingRef.current || native.isComposing || native.keyCode === 229) return;
    if (slashOpen) {
      if (event.key === "ArrowDown" || event.key === "ArrowUp") {
        event.preventDefault();
        if (slashSkills.length) {
          setSlashIndex((current) => (current + (event.key === "ArrowDown" ? 1 : -1) + slashSkills.length) % slashSkills.length);
        }
        return;
      }
      if (event.key === "Escape") {
        event.preventDefault();
        setSlashDismissed(true);
        return;
      }
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        const skill = slashSkills[slashIndex];
        if (skill) selectSlashSkill(skill);
        else setSlashDismissed(true);
        return;
      }
    }
    if (event.key !== "Enter" || event.shiftKey) return;
    onSubmit(event);
  };
  const handleSubmit = (event: React.FormEvent) => {
    if (slashOpen) {
      event.preventDefault();
      const skill = slashSkills[slashIndex];
      if (skill) selectSlashSkill(skill);
      else setSlashDismissed(true);
      return;
    }
    onSubmit(event);
  };
  return (
    <form className="composer" id="composer" onSubmit={handleSubmit} aria-busy={submitting || settling}>
      <input
        ref={fileInputRef}
        type="file"
        hidden
        onChange={(event) => {
          const file = event.currentTarget.files?.[0];
          event.currentTarget.value = "";
          if (file) onUploadAsset(file).catch((error) => window.alert(error instanceof Error ? error.message : "上传失败"));
        }}
      />
      {/* 输入卡片:textarea + 工具条,独立成一张卡片(边框/背景/阴影),与下方项目栏分离。 */}
      <div className="composer-input">
        {slashOpen && (
          <SkillSlashMenu skills={slashSkills} activeIndex={slashIndex} onSelect={selectSlashSkill} menuRef={slashMenuRef} />
        )}
        {selectedSkill && (
          <SkillIntentChip selectedSkill={selectedSkill} onClear={() => setSelectedSkill(null)} disabled={submitting || settling} />
        )}
        <textarea
          ref={textareaRef}
          value={prompt}
          onChange={(event) => {
            const nextPrompt = event.target.value;
            const completedToken = nextPrompt.match(/^\/([^\s]+)\s+([\s\S]*)$/);
            if (completedToken) {
              const exactName = completedToken[1].toLowerCase();
              const exactMatches = skills.filter((skill) => skill.name.toLowerCase() === exactName);
              if (exactMatches.length === 1) {
                setSelectedSkill(selectedSkillFromDefinition(exactMatches[0]));
                setPrompt(completedToken[2]);
                setSlashDismissed(true);
                return;
              }
            }
            setSlashDismissed(false);
            setPrompt(nextPrompt);
          }}
          placeholder="有问题尽管问。"
          rows={1}
          readOnly={submitting || settling}
          aria-autocomplete="list"
          aria-controls={slashOpen ? "composer-skill-list" : undefined}
          aria-expanded={slashOpen}
          aria-activedescendant={slashOpen && slashSkills.length ? `composer-skill-option-${slashIndex}` : undefined}
          onCompositionStart={() => { composingRef.current = true; }}
          onCompositionEnd={() => { composingRef.current = false; }}
          onKeyDown={handleKeyDown}
        />
        <div className="composer-toolbar">
          <button
            ref={plusTriggerRef}
            className="composer-plus"
            type="button"
            aria-label="添加上下文"
            title="添加上下文"
            aria-haspopup="menu"
            aria-expanded={plusOpen}
            aria-controls="composer-plus-menu"
            disabled={submitting || settling}
            onClick={() => plusOpen ? plusMenu.closeMenu() : plusMenu.openMenu()}
            onKeyDown={plusMenu.onTriggerKeyDown}
          >
            <Plus size={20} />
          </button>
          <div className="composer-tools">
            <RetrievalSelector retrievalMode={retrievalMode} setRetrievalMode={setRetrievalMode} />
            <ThinkingModeSelector thinkingMode={thinkingMode} setThinkingMode={setThinkingMode} />
            <button className="voice-button" type="button" aria-label="语音输入"><Mic size={17} /></button>
            {submitting ? (
              <button className="send-button submitting" type="button" aria-label="发送中" title="发送中" disabled>
                <LoaderCircle className="send-button-spinner" size={18} />
              </button>
            ) : settling ? (
              <button className="send-button submitting" type="button" aria-label="保存中" title="保存中" disabled>
                <LoaderCircle className="send-button-spinner" size={18} />
              </button>
            ) : running ? (
              <button className="send-button stop" type="button" aria-label="停止" onClick={onStop}><Square size={18} /></button>
            ) : (
              <button className={`send-button${prompt.trim() ? "" : " is-empty"}`} type="submit" aria-label="发送"><ArrowUp size={18} /></button>
            )}
          </div>
          {plusOpen && (
            <ComposerMenu
              menuRef={plusMenuRef}
              fileInputRef={fileInputRef}
              skills={skills}
              setSelectedSkill={setSelectedSkill}
              onClose={() => plusMenu.closeMenu()}
              focusMenuItem={plusMenu.focusMenuItem}
              moveMenuFocus={plusMenu.moveMenuFocus}
              onSaveLinkAsset={onSaveLinkAsset}
              onAttachRecent={onAttachRecent}
              onManageSkills={onManageSkills}
            />
          )}
        </div>
      </div>
      {/* 项目选择栏:独立于输入卡片,tuck 在其下方(参考 Codex 的分离阴影区)。 */}
      <ProjectChip />
    </form>
  );
}
